import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  Container,
  Paper,
  TextField,
  MenuItem,
  Button,
  Grid,
  Alert,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Card,
  CardContent,
  Divider,
  Chip,
  FormControl,
  InputLabel,
  Select,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  Print as PrintIcon
} from '@mui/icons-material';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const TaxComparisonDisplay = ({ employee_id, financial_year }) => {
  const [taxData, setTaxData] = useState([]);
  const [allowancesData, setAllowancesData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("");
  const [error, setError] = useState(null);
  const [employeeId, setEmployeeId] = useState(employee_id || '');
  const [financialYear, setFinancialYear] = useState(financial_year || '');
  const [selectedMonth, setSelectedMonth] = useState('');
  const [availableMonths, setAvailableMonths] = useState([]);
  const [availableYears, setAvailableYears] = useState([]);
  const [calculationModes, setCalculationModes] = useState([]);

  // ========== ALL MONTHS (April to March - Financial Year Order) ==========
  const allMonths = ['April', 'May', 'June', 'July', 'August', 'September', 
                     'October', 'November', 'December', 'January', 'February', 'March'];

  // Generate financial years dynamically (last 10 years)
  useEffect(() => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let i = 0; i < 10; i++) {
      const year = currentYear - i;
      years.push(`${year}-${String(year + 1)}`);
    }
    setAvailableYears(years);
  }, []);

  useEffect(() => {
    if (employee_id !== undefined && employee_id !== null) {
      setEmployeeId(employee_id);
    }
    if (financial_year !== undefined && financial_year !== null) {
      setFinancialYear(financial_year);
    }
  }, [employee_id, financial_year]);

  // Fetch data when props are provided
  useEffect(() => {
    if (employeeId && financialYear && selectedMonth) {
      fetchTaxData();
    }
  }, [employeeId, financialYear, selectedMonth]);

  // When tax data is loaded, extract available months and calculation modes
  useEffect(() => {
    if (taxData && taxData.length > 0) {
      // Extract unique months from data
      const months = [...new Set(taxData.map(item => item.month))].filter(Boolean);
      setAvailableMonths(months);
      
      // Extract unique calculation modes
      const modes = [...new Set(taxData.map(item => item.calculation_mode))].filter(Boolean);
      setCalculationModes(modes);
      
      // Set first month as default if available and no month selected
      if (months.length > 0 && !selectedMonth) {
        setSelectedMonth(months[0]);
      }
      
      // Set first mode as default if available and no tab selected
      if (modes.length > 0 && !activeTab) {
        setActiveTab(modes[0]);
      }
    }
  }, [taxData]);

  const fetchTaxData = async () => {
    if (!employeeId) {
      setError('Please enter an employee ID');
      return;
    }
    if (!financialYear) {
      setError('Please select a financial year');
      return;
    }
    if (!selectedMonth) {
      setError('Please select a month');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        alert("No authentication token found. Please log in.");
        return;
      }

      setLoading(true);
      setError(null);

      // Build URL with month parameter
      const url = `${API_URL}/api/payrolltax/calculatedtax/${employeeId}/${financialYear}/${selectedMonth}`;

      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        withCredentials: true
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || `API error: ${response.status}`);
      }

      const result = await response.json();
      
      if (result.data && result.data.length > 0) {
        setTaxData(result.data);
        
        // Extract months and modes from data
        const months = [...new Set(result.data.map(item => item.month))].filter(Boolean);
        setAvailableMonths(months);
        
        const modes = [...new Set(result.data.map(item => item.calculation_mode))].filter(Boolean);
        setCalculationModes(modes);
        
        // If no tab selected and modes available, select first
        if (modes.length > 0 && !activeTab) {
          setActiveTab(modes[0]);
        }
      } else {
        setTaxData([]);
        setAvailableMonths([]);
        setCalculationModes([]);
        setError('No tax data found for this employee and financial year');
      }

      // Fetch allowances data
      const allowancesResponse = await fetch(`${API_URL}/api/allowances?employee_id=${employeeId}&financial_year=${financialYear}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        withCredentials: true
      });

      if (allowancesResponse.ok) {
        const allowancesResult = await allowancesResponse.json();
        setAllowancesData(allowancesResult.data);
      }

      setLoading(false);
    } catch (err) {
      setError(`Failed to fetch data: ${err.message}`);
      setLoading(false);
    }
  };

  // Handle month change - refetch data for selected month
  const handleMonthChange = (event) => {
    const newMonth = event.target.value;
    setSelectedMonth(newMonth);
    // Refetch data for the selected month
    if (newMonth && employeeId && financialYear) {
      fetchTaxDataForMonth(newMonth);
    }
  };

  // Fetch data for specific month
  const fetchTaxDataForMonth = async (month) => {
    if (!employeeId || !financialYear || !month) return;

    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      setLoading(true);
      setError(null);

      const url = `${API_URL}/api/payrolltax/calculatedtax/${employeeId}/${financialYear}/${month}`;
      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        withCredentials: true
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || `API error: ${response.status}`);
      }

      const result = await response.json();

      if (result.data && result.data.length > 0) {
        setTaxData(result.data);
        
        // Update calculation modes from new data
        const modes = [...new Set(result.data.map(item => item.calculation_mode))].filter(Boolean);
        setCalculationModes(modes);
        
        // Set first mode as active if current tab not in new data
        if (modes.length > 0 && (!activeTab || !modes.includes(activeTab))) {
          setActiveTab(modes[0]);
        }
      } else {
        setTaxData([]);
        setCalculationModes([]);
        setError(`No tax data found for ${month} ${financialYear}`);
      }

      setLoading(false);
    } catch (err) {
      setError(`Failed to fetch data for ${month}: ${err.message}`);
      setLoading(false);
    }
  };

  // Format currency values
  const formatCurrency = (value) => {
    const numValue = Number(value);
    if (isNaN(numValue) || numValue === 0) {
      return '₹0';
    }
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
      minimumFractionDigits: 0
    }).format(numValue);
  };

  // Get the filtered data based on active tab
  const getFilteredData = () => {
    if (!activeTab) return [];
    return taxData.filter(item => item.calculation_mode === activeTab);
  };

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // Get month-wise summary for comparison
  const getMonthSummary = () => {
    if (!taxData || taxData.length === 0) return null;
    
    const data = taxData.find(item => item.calculation_mode === activeTab);
    if (!data) return null;

    return {
      month: data.month,
      months_paid: data.months_paid || 0,
      tax_paid_so_far: data.tax_paid_so_far || 0,
      remaining_months: data.remaining_months || 0,
      remaining_tax: data.remaining_tax || 0,
      cumulative_gross_ytd: data.cumulative_gross_ytd || 0,
      projected_annual_income: data.projected_annual_income || 0,
      total_tax: data.total_tax_payable_new_regime || data.total_tax_payable_old_regime || 0
    };
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h5" gutterBottom  textAlign="center" sx={{ mb: 4 }}>
        Tax Comparison & Computation
      </Typography>

      {/* Search Form */}
      <Paper elevation={2} sx={{ p: 3, mb: 4 }}>
        <Grid container spacing={3} alignItems="flex-end">
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="Employee ID"
              variant="outlined"
              value={employeeId}
              onChange={(e) => setEmployeeId(e.target.value)}
              placeholder="Enter employee ID"
              disabled={!!employee_id}
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel id="financial-year-label">Financial Year</InputLabel>
              <Select
                labelId="financial-year-label"
                value={financialYear}
                onChange={(e) => setFinancialYear(e.target.value)}
                label="Financial Year"
              >
                {availableYears.map(year => (
                  <MenuItem key={year} value={year}>{year}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* ========== MONTH DROPDOWN - Shows ALL months ========== */}
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel id="month-select-label">Month</InputLabel>
              <Select
                labelId="month-select-label"
                value={selectedMonth}
                onChange={handleMonthChange}
                label="Month"
              >
                <MenuItem value="" disabled>Select a month</MenuItem>
                {allMonths.map(month => (
                  <MenuItem key={month} value={month}>
                    {month}
                    {/* Show chip if data exists for this month */}
                    {availableMonths.includes(month) && (
                      <Chip 
                        label="✓" 
                        size="small" 
                        color="success" 
                        sx={{ ml: 1, height: 16, fontSize: '0.6rem' }}
                      />
                    )}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={3}>
            <Button
              fullWidth
              variant="contained"
              color="primary"
              onClick={fetchTaxData}
              disabled={loading || !employeeId || !financialYear || !selectedMonth}
              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <RefreshIcon />}
            >
              {loading ? 'Loading...' : 'Compute Tax'}
            </Button>
          </Grid>
        </Grid>

        {/* Month Info Chip and Summary */}
        {selectedMonth && taxData.length > 0 && (
          <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Chip 
              label={`Month: ${selectedMonth}`} 
              color="primary" 
              variant="outlined"
            />
            <Chip 
              label={`Total Records: ${taxData.length}`} 
              color="info" 
              variant="outlined"
            />
            {calculationModes.map(mode => (
              <Chip 
                key={mode}
                label={mode} 
                color={activeTab === mode ? 'success' : 'default'}
                size="small"
                variant={activeTab === mode ? 'filled' : 'outlined'}
              />
            ))}
      
          </Box>
        )}

        {error && (
          <Box mt={2}>
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          </Box>
        )}
      </Paper>

      {taxData && taxData.length > 0 && (
        <Box>
          <Typography variant="h5" component="h2" gutterBottom fontWeight="bold" textAlign="center">
            Computed Sheet - {selectedMonth || 'All Months'}
          </Typography>

          {/* Tab Navigation - Dynamically generated from data */}
          {calculationModes.length > 1 && (
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
              <Tabs 
                value={activeTab}
                onChange={handleTabChange}
                aria-label="tax calculation mode tabs"
                variant="scrollable"
                scrollButtons="auto"
              >
                {calculationModes.map(mode => (
                  <Tab 
                    key={mode} 
                    label={mode.charAt(0).toUpperCase() + mode.slice(1)} 
                    value={mode} 
                  />
                ))}
              </Tabs>
            </Box>
          )}

          {getFilteredData().length > 0 ? (
            <>
              {/* Tax Comparison Table */}
              <Paper elevation={2} sx={{ mb: 4 }}>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow sx={{ bgcolor: 'grey.100' }}>
                        <TableCell sx={{ fontWeight: 'bold', width: '40%' }}>Particulars</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>New Regime</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>Old Regime</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {/* ... rest of the table rows ... */}
                      <TableRow hover>
                        <TableCell component="th" scope="row">Gross Salary</TableCell>
                        <TableCell align="right">
                          {formatCurrency(getFilteredData()[0].gross_salary_new_regime)}
                        </TableCell>
                        <TableCell align="right">
                          {formatCurrency(getFilteredData()[0].gross_salary_old_regime)}
                        </TableCell>
                      </TableRow>
                      <TableRow hover>
                        <TableCell component="th" scope="row">Exemption U/S 10</TableCell>
                        <TableCell align="right">
                          {formatCurrency(getFilteredData()[0].exemption_us_10_new_regime)}
                        </TableCell>
                        <TableCell align="right">
                          {formatCurrency(getFilteredData()[0].exemption_us_10_old_regime)}
                        </TableCell>
                      </TableRow>
                      <TableRow hover>
                        <TableCell component="th" scope="row">Net Salary (After Section 10)</TableCell>
                        <TableCell align="right">
                          {formatCurrency(getFilteredData()[0].net_salary_after_section_10_new_regime)}
                        </TableCell>
                        <TableCell align="right">
                          {formatCurrency(getFilteredData()[0].net_salary_after_section_10_old_regime)}
                        </TableCell>
                      </TableRow>
                      <TableRow hover>
                        <TableCell component="th" scope="row">Standard Deduction & Professional Tax</TableCell>
                        <TableCell align="right">
                          {formatCurrency(getFilteredData()[0].standard_deduction_and_professional_tax_new_regime)}
                        </TableCell>
                        <TableCell align="right">
                          {formatCurrency(getFilteredData()[0].standard_deduction_and_professional_tax_old_regime)}
                        </TableCell>
                      </TableRow>
                      <TableRow hover>
                        <TableCell component="th" scope="row">Net Taxable Salary</TableCell>
                        <TableCell align="right">
                          {formatCurrency(getFilteredData()[0].net_taxable_salary_new_regime)}
                        </TableCell>
                        <TableCell align="right">
                          {formatCurrency(getFilteredData()[0].net_taxable_salary_old_regime)}
                        </TableCell>
                      </TableRow>
                      <TableRow hover>
                        <TableCell component="th" scope="row">Loss From House Property</TableCell>
                        <TableCell align="right">
                          {formatCurrency(getFilteredData()[0].house_property_loss_new_regime)}
                        </TableCell>
                        <TableCell align="right">
                          {formatCurrency(getFilteredData()[0].house_property_loss_old_regime)}
                        </TableCell>
                      </TableRow>
                      <TableRow hover>
                        <TableCell component="th" scope="row">Income From Other Sources</TableCell>
                        <TableCell align="right">
                          {formatCurrency(getFilteredData()[0].income_from_other_sources_new_regime)}
                        </TableCell>
                        <TableCell align="right">
                          {formatCurrency(getFilteredData()[0].income_from_other_sources_old_regime)}
                        </TableCell>
                      </TableRow>
                      <TableRow hover>
                        <TableCell component="th" scope="row">Deduction Under Chapter VI-A</TableCell>
                        <TableCell align="right">
                          {formatCurrency(getFilteredData()[0].deduction_under_chapter_vi_a_new_regime)}
                        </TableCell>
                        <TableCell align="right">
                          {formatCurrency(getFilteredData()[0].deduction_under_chapter_vi_a_old_regime)}
                        </TableCell>
                      </TableRow>
                      <TableRow hover sx={{ bgcolor: 'grey.100' }}>
                        <TableCell component="th" scope="row">
                          <Typography fontWeight="bold">Taxable Income</Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography fontWeight="bold">
                            {formatCurrency(getFilteredData()[0].taxable_income_new_regime)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography fontWeight="bold">
                            {formatCurrency(getFilteredData()[0].taxable_income_old_regime)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                      <TableRow hover>
                        <TableCell component="th" scope="row">Tax Payable on Total Income</TableCell>
                        <TableCell align="right">
                          {formatCurrency(getFilteredData()[0].tax_payable_on_total_income_new_regime)}
                        </TableCell>
                        <TableCell align="right">
                          {formatCurrency(getFilteredData()[0].tax_payable_on_total_income_old_regime)}
                        </TableCell>
                      </TableRow>
                      <TableRow hover>
                        <TableCell component="th" scope="row">Rebate U/S 87A</TableCell>
                        <TableCell align="right">
                          {formatCurrency(getFilteredData()[0].rebate_us_87a_new_regime)}
                        </TableCell>
                        <TableCell align="right">
                          {formatCurrency(getFilteredData()[0].rebate_us_87a_old_regime)}
                        </TableCell>
                      </TableRow>
                      <TableRow hover>
                        <TableCell component="th" scope="row">Tax After Rebate</TableCell>
                        <TableCell align="right">
                          {formatCurrency(getFilteredData()[0].tax_payable_after_section_87a_rebate_new_regime)}
                        </TableCell>
                        <TableCell align="right">
                          {formatCurrency(getFilteredData()[0].tax_payable_after_section_87a_rebate_old_regime)}
                        </TableCell>
                      </TableRow>
                      <TableRow hover>
                        <TableCell component="th" scope="row">Education Cess</TableCell>
                        <TableCell align="right">
                          {formatCurrency(getFilteredData()[0].cess_new_regime)}
                        </TableCell>
                        <TableCell align="right">
                          {formatCurrency(getFilteredData()[0].cess_old_regime)}
                        </TableCell>
                      </TableRow>
                      <TableRow hover sx={{ bgcolor: 'grey.100' }}>
                        <TableCell component="th" scope="row">
                          <Typography fontWeight="bold">Total Tax Payable</Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography fontWeight="bold" color="primary">
                            {formatCurrency(getFilteredData()[0].total_tax_payable_new_regime)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography fontWeight="bold" color="secondary">
                            {formatCurrency(getFilteredData()[0].total_tax_payable_old_regime)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                      <TableRow hover>
                        <TableCell component="th" scope="row">Tax Recovered TDS</TableCell>
                        <TableCell align="right">
                          {formatCurrency(getFilteredData()[0].tax_recovered_tds_new_regime)}
                        </TableCell>
                        <TableCell align="right">
                          {formatCurrency(getFilteredData()[0].tax_recovered_tds_old_regime)}
                        </TableCell>
                      </TableRow>
                      <TableRow hover>
                        <TableCell component="th" scope="row">Tax Recovered TCS</TableCell>
                        <TableCell align="right">
                          {formatCurrency(getFilteredData()[0].tax_recovered_tcs_new_regime)}
                        </TableCell>
                        <TableCell align="right">
                          {formatCurrency(getFilteredData()[0].tax_recovered_tcs_old_regime)}
                        </TableCell>
                      </TableRow>
                      <TableRow hover sx={{ bgcolor: '#e3f2fd' }}>
                        <TableCell component="th" scope="row">
                          <Typography fontWeight="bold">Balance Tax Payable</Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography fontWeight="bold" color="primary">
                            {formatCurrency(
                              getFilteredData()[0].total_tax_payable_new_regime - 
                              getFilteredData()[0].tax_recovered_tds_new_regime
                            )}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography fontWeight="bold" color="secondary">
                            {formatCurrency(
                              getFilteredData()[0].total_tax_payable_old_regime - 
                              getFilteredData()[0].tax_recovered_tds_old_regime
                            )}
                          </Typography>
                        </TableCell>
                      </TableRow>
                      <TableRow hover>
                        <TableCell component="th" scope="row">Monthly Tax</TableCell>
                        <TableCell align="right">
                          {formatCurrency(getFilteredData()[0].tax_per_month_new_regime)}
                        </TableCell>
                        <TableCell align="right">
                          {formatCurrency(getFilteredData()[0].tax_per_month_old_regime)}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>

              {/* Tax Liability Summary */}
              <Paper elevation={3} sx={{ mt: 4, borderTop: '4px solid #1976d2' }}>
                <Box p={3}>
                  <Typography variant="h5" align="center" gutterBottom fontWeight="medium">
                    Tax Liability Summary
                  </Typography>
                  
                  <Grid container spacing={3} mb={3}>
                    <Grid item xs={12} md={6}>
                      <Box textAlign="center" sx={{ p: 2, bgcolor: '#e8f5e9', borderRadius: 1 }}>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          Better Regime
                        </Typography>
                        <Typography variant="h6" color="success.main" fontWeight="bold">
                          {getFilteredData()[0].better_regime}
                        </Typography>
                      </Box>
                    </Grid>
                    
                    <Grid item xs={12} md={6}>
                      <Box textAlign="center" sx={{ p: 2, bgcolor: '#fff3e0', borderRadius: 1 }}>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          Tax Difference
                        </Typography>
                        <Typography variant="h6" color="warning.main" fontWeight="bold">
                          {formatCurrency(getFilteredData()[0].tax_difference_amount)}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
      
                </Box>
              </Paper>
            </>
          ) : (
            <Paper elevation={2} sx={{ p: 4, textAlign: 'center' }}>
              <Typography color="text.secondary">
                No data available for {activeTab || 'selected'} mode
              </Typography>
            </Paper>
          )}
        </Box>
      )}

      {!loading && taxData.length === 0 && !error && (
        <Paper elevation={2} sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">
            Enter an employee ID, select a financial year and month, then click "Compute Tax" to view tax comparison data
          </Typography>
        </Paper>
      )}
      
    </Container>
  );
};

export default TaxComparisonDisplay;